import React, { useEffect } from 'react';
import GenericTemplate from '../components/templates/GenericTemplate';

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

// xross interface
import { XROSS_USER_PROFILE, Feature } from '../common/interface';

// Firebase アクセス（外出し）
import {
  getUserProfileByUid,
  updateUserFeatures, // features 直下のパッチを受け取り、features.xxx へ扇状展開する実装を想定
} from '../common/firebase/userFeatures';

import { nowTimestamp, timestamp } from '../firebase';

/* ================================
 * 型ユーティリティ（UI用に固定）
 * ================================ */

type PremiumTier = 'basic' | 'pro' | 'secret';

type Features = {
  premiumMenu: Feature<PremiumTier>;
  avatarFrame: Feature<string>;
  xrPost: Feature<boolean>;
  blockedBy: Feature<boolean>; // ★ XR投稿と同じON/OFF運用に変更
};

type FeatureKey = keyof Features;

// value まで型が狭まる type guard
type EnabledFeature<T> = Extract<Feature<T>, { state: 'allowed' | 'forced' }>;
export function isEnabled<T>(f: Feature<T>): f is EnabledFeature<T> {
  return f?.state === 'allowed' || f?.state === 'forced';
}

// denied の既定オブジェクト（updatedAt は Timestamp を入れる）
export function denied<T>(): Feature<T> {
  return { state: 'denied', updatedAt: nowTimestamp() };
}

// Firestore の生データを UI 用 Features に正規化
export function normalizeFeatures(
  f: XROSS_USER_PROFILE['features'] | undefined
): Features {
  return {
    premiumMenu: f?.premiumMenu ?? denied<PremiumTier>(),
    avatarFrame: f?.avatarFrame ?? denied<string>(),
    xrPost: f?.xrPost ?? denied<boolean>(),
    blockedBy: f?.blockedBy ?? denied<boolean>(), // ★ boolean で正規化
  };
}

/* ================================
 * コンポーネント
 * ================================ */

const PremiumSettings: React.FC = () => {
  // ----- 検索/状態 -----
  const [uidInput, setUidInput] = React.useState('');
  const [user, setUser] = React.useState<XROSS_USER_PROFILE | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const filledFeatures = React.useMemo<Features | null>(
    () => (user ? normalizeFeatures(user.features) : null),
    [user]
  );

  // ----- 編集用ローカル状態 -----
  const [localPremiumState, setLocalPremiumState] = React.useState<
    'denied' | 'allowed' | 'forced'
  >('denied');
  const [localPremiumValue, setLocalPremiumValue] =
    React.useState<PremiumTier>('basic');

  const [localAvatarState, setLocalAvatarState] = React.useState<
    'denied' | 'allowed' | 'forced'
  >('denied');
  const [localAvatarValue, setLocalAvatarValue] = React.useState<string>('');

  const [localXrState, setLocalXrState] = React.useState<
    'denied' | 'allowed' | 'forced'
  >('denied');
  const [localXrValue, setLocalXrValue] = React.useState<boolean>(false);

  // ★ blockedBy（XR投稿と同じON/OFF）
  const [localBlockedByState, setLocalBlockedByState] = React.useState<
    'denied' | 'allowed' | 'forced'
  >('denied');
  const [localBlockedByValue, setLocalBlockedByValue] =
    React.useState<boolean>(false);

  // 読み込み
  const fetchUser = React.useCallback(async () => {
    if (!uidInput) return;
    setError(null);
    setLoading(true);
    try {
      const data = await getUserProfileByUid(uidInput);
      if (!data) {
        setUser(null);
        setError('ユーザが見つかりませんでした。UID を確認してください。');
      } else {
        // Firestore 側で id を持っていない場合は UID をセット
        setUser({ ...data, id: data.id || uidInput });
      }
    } catch (e: any) {
      console.error(e);
      setError(e?.message ?? '読み込み中にエラーが発生しました。');
    } finally {
      setLoading(false);
    }
  }, [uidInput]);

  // 読み込み後にローカル状態へ反映
  useEffect(() => {
    if (!filledFeatures) return;
    const ff = filledFeatures;

    // premium
    setLocalPremiumState(ff.premiumMenu.state);
    if (isEnabled(ff.premiumMenu)) {
      setLocalPremiumValue(ff.premiumMenu.value);
    } else {
      setLocalPremiumValue('basic');
    }

    // avatar
    setLocalAvatarState(ff.avatarFrame.state);
    if (isEnabled(ff.avatarFrame)) {
      setLocalAvatarValue(ff.avatarFrame.value);
    } else {
      setLocalAvatarValue('');
    }

    // xrPost
    setLocalXrState(ff.xrPost.state);
    setLocalXrValue(isEnabled(ff.xrPost) ? Boolean(ff.xrPost.value) : false);

    // blockedBy（ON/OFF）
    setLocalBlockedByState(ff.blockedBy.state);
    setLocalBlockedByValue(
      isEnabled(ff.blockedBy) ? Boolean(ff.blockedBy.value) : false
    );
  }, [filledFeatures]);

  // ----- 確認ダイアログ -----
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmPayloadKey, setConfirmPayloadKey] = React.useState<
    'premiumMenu' | 'avatarFrame' | 'xrPost' | 'blockedBy' | null
  >(null);

  const openConfirm = (
    key: 'premiumMenu' | 'avatarFrame' | 'xrPost' | 'blockedBy'
  ) => {
    setConfirmPayloadKey(key);
    setConfirmOpen(true);
  };

  // 指定キーだけ updatedAt を更新して差し替える
  function makeNextFeatures(
    current: Features,
    key: FeatureKey,
    next:
      | { state: 'denied' }
      | {
          state: 'allowed' | 'forced';
          value: PremiumTier | string | boolean;
        }
  ): Features {
    const out: Features = { ...current };
    (out as any)[key] =
      next.state === 'denied'
        ? ({ state: 'denied', updatedAt: timestamp } as Feature<any>)
        : ({
            state: next.state,
            value: (next as any).value,
            updatedAt: timestamp,
          } as Feature<any>);
    return out;
  }

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (!user || !confirmPayloadKey || !filledFeatures) return;

    try {
      let nextFeatures: Features = filledFeatures;

      if (confirmPayloadKey === 'premiumMenu') {
        const payload =
          localPremiumState === 'denied'
            ? { state: 'denied' as const }
            : {
                state: localPremiumState as 'allowed' | 'forced',
                value: localPremiumValue as PremiumTier,
              };
        nextFeatures = makeNextFeatures(filledFeatures, 'premiumMenu', payload);
      }

      if (confirmPayloadKey === 'avatarFrame') {
        const payload =
          localAvatarState === 'denied'
            ? { state: 'denied' as const }
            : {
                state: localAvatarState as 'allowed' | 'forced',
                value: localAvatarValue as string,
              };
        nextFeatures = makeNextFeatures(filledFeatures, 'avatarFrame', payload);
      }

      if (confirmPayloadKey === 'xrPost') {
        const payload =
          localXrState === 'denied'
            ? { state: 'denied' as const }
            : {
                state: localXrState as 'allowed' | 'forced',
                value: !!localXrValue,
              };
        nextFeatures = makeNextFeatures(filledFeatures, 'xrPost', payload);
      }

      if (confirmPayloadKey === 'blockedBy') {
        const payload =
          localBlockedByState === 'denied'
            ? { state: 'denied' as const }
            : {
                state: localBlockedByState as 'allowed' | 'forced',
                value: !!localBlockedByValue, // ★ boolean を保存
              };
        nextFeatures = makeNextFeatures(filledFeatures, 'blockedBy', payload);
      }

      // ★ features 全体を渡す（内部で features.xxx に扇状展開して update する実装を想定）
      const ok = await updateUserFeatures(user.id, nextFeatures as any);
      if (!ok) setError('更新に失敗しました。');
      else await fetchUser(); // 再読込
    } catch (e: any) {
      console.error(e);
      setError(e?.message ?? '更新中にエラーが発生しました。');
    } finally {
      setConfirmPayloadKey(null);
    }
  };

  return (
    <GenericTemplate title="拡張機能 管理（ユーザ単位）">
      <Stack spacing={2}>
        {/* UID 入力 → 読み込み */}
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="UID を入力"
            value={uidInput}
            onChange={(e) => setUidInput(e.target.value.trim())}
            size="small"
            sx={{ minWidth: 360 }}
          />
          <Button
            variant="contained"
            onClick={fetchUser}
            disabled={!uidInput || loading}
          >
            {loading ? '読込中…' : '読み込む'}
          </Button>
        </Box>

        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {/* ユーザ基本情報 */}
        {user && filledFeatures && (
          <Box>
            <Typography variant="h6" gutterBottom>
              ユーザ情報
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">UID：{user.id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  ユーザ名：{user.xrossUserName ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  ユーザID：{user.xrossUserID ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  email：{user.email ?? '-'}
                </Typography>
              </Grid>

              {/* Premium */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  Premium：
                  {isEnabled(filledFeatures.premiumMenu) ? '有効' : '無効'}
                  {isEnabled(filledFeatures.premiumMenu) && (
                    <>（{String(filledFeatures.premiumMenu.value)}）</>
                  )}
                </Typography>
              </Grid>

              {/* AvatarFrame */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  AvatarFrame：
                  {isEnabled(filledFeatures.avatarFrame) ? '有効' : '無効'}
                  {isEnabled(filledFeatures.avatarFrame) && (
                    <>（{String(filledFeatures.avatarFrame.value)}）</>
                  )}
                </Typography>
              </Grid>

              {/* XR投稿 */}
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="body2">
                  XR投稿：
                  {isEnabled(filledFeatures.xrPost) ? '有効' : '無効'}
                </Typography>
              </Grid>

              {/* blockedBy（ON/OFF表記） */}
              <Grid item xs={12}>
                <Typography variant="body2">
                  blockedBy：
                  {isEnabled(filledFeatures.blockedBy) ? '有効' : '無効'}
                  {isEnabled(filledFeatures.blockedBy) && (
                    <>（{filledFeatures.blockedBy.value ? 'ON' : 'OFF'}）</>
                  )}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {/* 編集フォーム */}
        {user && filledFeatures && (
          <Stack spacing={3}>
            {/* Premium */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Premium メニュー
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
              >
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="premium-state-label">状態</InputLabel>
                  <Select
                    labelId="premium-state-label"
                    value={localPremiumState}
                    label="状態"
                    onChange={(e) =>
                      setLocalPremiumState(e.target.value as any)
                    }
                  >
                    <MenuItem value="denied">denied</MenuItem>
                    <MenuItem value="allowed">allowed</MenuItem>
                    <MenuItem value="forced">forced</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ minWidth: 160 }}
                  disabled={localPremiumState === 'denied'}
                >
                  <InputLabel id="premium-tier-label">Tier</InputLabel>
                  <Select
                    labelId="premium-tier-label"
                    value={localPremiumValue}
                    label="Tier"
                    onChange={(e) =>
                      setLocalPremiumValue(e.target.value as PremiumTier)
                    }
                  >
                    <MenuItem value="basic">basic</MenuItem>
                    <MenuItem value="pro">pro</MenuItem>
                    <MenuItem value="secret">secret</MenuItem>
                  </Select>
                </FormControl>

                <Chip
                  label="保存"
                  color="primary"
                  onClick={() => openConfirm('premiumMenu')}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            </Box>

            {/* AvatarFrame */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                アバターフレーム
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
              >
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="avatar-state-label">状態</InputLabel>
                  <Select
                    labelId="avatar-state-label"
                    value={localAvatarState}
                    label="状態"
                    onChange={(e) => setLocalAvatarState(e.target.value as any)}
                  >
                    <MenuItem value="denied">denied</MenuItem>
                    <MenuItem value="allowed">allowed</MenuItem>
                    <MenuItem value="forced">forced</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="frameId"
                  size="small"
                  sx={{ minWidth: 280 }}
                  value={localAvatarValue}
                  onChange={(e) => setLocalAvatarValue(e.target.value)}
                  disabled={localAvatarState === 'denied'}
                />

                <Chip
                  label="保存"
                  color="primary"
                  onClick={() => openConfirm('avatarFrame')}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            </Box>

            {/* XR Post */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                XR 投稿
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
              >
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="xr-state-label">状態</InputLabel>
                  <Select
                    labelId="xr-state-label"
                    value={localXrState}
                    label="状態"
                    onChange={(e) => setLocalXrState(e.target.value as any)}
                  >
                    <MenuItem value="denied">denied</MenuItem>
                    <MenuItem value="allowed">allowed</MenuItem>
                    <MenuItem value="forced">forced</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={localXrState !== 'denied' && !!localXrValue}
                      onChange={(e) => setLocalXrValue(e.target.checked)}
                      disabled={localXrState === 'denied'}
                    />
                  }
                  label="value（true = 解禁）"
                />

                <Chip
                  label="保存"
                  color="primary"
                  onClick={() => openConfirm('xrPost')}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            </Box>

            {/* blockedBy（ON/OFF） */}
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                blockedBy（ON / OFF）
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
              >
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="blockedby-state-label">状態</InputLabel>
                  <Select
                    labelId="blockedby-state-label"
                    value={localBlockedByState}
                    label="状態"
                    onChange={(e) =>
                      setLocalBlockedByState(e.target.value as any)
                    }
                  >
                    <MenuItem value="denied">denied</MenuItem>
                    <MenuItem value="allowed">allowed</MenuItem>
                    <MenuItem value="forced">forced</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        localBlockedByState !== 'denied' &&
                        !!localBlockedByValue
                      }
                      onChange={(e) => setLocalBlockedByValue(e.target.checked)}
                      disabled={localBlockedByState === 'denied'}
                    />
                  }
                  label="value（true = ON）"
                />

                <Chip
                  label="保存"
                  color="primary"
                  onClick={() => openConfirm('blockedBy')}
                  sx={{ cursor: 'pointer' }}
                />
              </Stack>
            </Box>
          </Stack>
        )}
      </Stack>

      {/* 確認ダイアログ */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>設定を保存しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            サーバタイムスタンプで更新されます（アクセサ側で付与）。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleConfirm}>
            保存
          </Button>
        </DialogActions>
      </Dialog>
    </GenericTemplate>
  );
};

export default PremiumSettings;
